/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, CircularProgress } from '@mui/material';

import { extractRoles, hasProjectPermission } from 'shared';

import { AlignCenter } from '../../../../components/AlignCenter/AlignCenter';
import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { SelectItemCatalogue } from '../../../../components/SelectItemCatalogue/SelectItemCatalogue';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { Namespaces } from '../../../../i18n';
import { ProjectApiService } from '../../../../services/ProjectApiService';
import { RootState } from '../../../../store';
import { enqueueHUSnackbar } from '../../../../store/notifier/actions';
import { useSelectEntityModalStyles } from '../../../../styles/selectEntityModal';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { EntityId } from '../../../../types/api/EntityId';
import { Organization } from '../../../../types/organization/Organization';
import { IProject } from '../../../../types/project/IProject';
import { useObjectState } from '../../../../utils/useObjectState';
import { CreateClusterValues } from '../../CreateClusterOverlay.types';

type SelectOrganizationProjectProps<Values> = FormikProps<Values> & object;

type State = {
  project?: IProject;
  organization?: Organization;
  isOpenModal: boolean;
  isFetchingProjects: boolean;
  projects: IProject[];
};

export function SelectOrganizationProject<Values extends CreateClusterValues>(
  props: SelectOrganizationProjectProps<Values>,
) {
  const semClasses = useSelectEntityModalStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation([Namespaces.CreateCluster, Namespaces.Common]);
  const { values, setValues, setFieldValue } = props;
  const { organizations, auth } = useSelector((state: RootState) => ({
    organizations: state.organizations,
    auth: state.auth,
  }));
  const [state, setState] = useObjectState<State>({
    organization: undefined,
    project: undefined,
    isOpenModal: false,
    isFetchingProjects: false,
    projects: [],
  });
  const { organization, project, isOpenModal, isFetchingProjects, projects } = state;

  const organizationsFiltered = useMemo(() => {
    if (values.allowedOrganizations && values.allowedOrganizations.length) {
      return organizations.options.filter(o => values.allowedOrganizations!.find(i => i === o.id));
    } else {
      return organizations.options;
    }
  }, [organizations.options, values.allowedOrganizations]);

  const fetchProjects = useCallback(
    (organizationId: EntityId) => {
      if (!organizationId) {
        setState({ isFetchingProjects: false });
        return;
      }
      setState({ isFetchingProjects: true });
      const fetchData = async () => {
        try {
          const result = await ProjectApiService.fetchProjects({ organizationId });
          const projects = result.data.payload.filter(project => {
            const roles = extractRoles(auth?.user?.organizations || [], organizationId, project.id);
            return hasProjectPermission(roles.organizationRoles, roles.projectRoles, 'create_cluster');
          });
          setState({ projects });
        } catch (e) {
          const error = e as ApiClientError;
          console.error(error);
          dispatch(
            enqueueHUSnackbar(
              {
                primaryText: t(`${Namespaces.Common}:errors.unexpected_error`),
              },
              { variant: 'error' },
            ),
          );
        } finally {
          setState({ isFetchingProjects: false });
        }
      };
      void fetchData();
    },
    [auth, dispatch, setState, t],
  );

  const setOrganization = useCallback(
    (org?: Organization) => {
      setState({ organization: org });
    },
    [setState],
  );

  const setProject = useCallback(
    (project?: IProject) => {
      setState({ project });
    },
    [setState],
  );

  useEffect(() => {
    if (organizations.isFetching) {
      return;
    }
    const currentOrganization = organizationsFiltered.find(v => v.id === values.organizationId);
    setOrganization(currentOrganization);
  }, [values.organizationId, organizations.isFetching, organizationsFiltered, isOpenModal, setOrganization]);

  useEffect(() => {
    if (!organization) {
      return;
    }
    fetchProjects(organization.id);
  }, [fetchProjects, organization]);

  useEffect(() => {
    const currentProject = projects.find(v => v.id === values.projectId);
    setProject(currentProject);
    // remove this if you dont need 100% auto-fill
    if (currentProject) {
      void setFieldValue('projectId', currentProject.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.projectId, projects, setProject, isOpenModal]);

  const onAddButtonClick = () => {
    setState({ isOpenModal: false });
    void setValues({
      ...values,
      project: project || null,
      projectId: project?.id,
      organizationId: organization?.id,
    });
  };

  const onCancel = () => {
    setState({ isOpenModal: false });
  };

  const onOrganizationSelect = (org: Organization) => {
    setOrganization(org);
  };

  const onProjectSelect = (project: IProject) => {
    setProject(project);
  };

  const getButtonText = () => {
    return project?.name || t('labels.select_project');
  };

  const buttonVariant = project ? 'contained' : 'outlined';

  const isDisabled = organizations.isFetching || isFetchingProjects;
  const shouldSkipOrganizationsColumn = useMemo<boolean>(() => {
    return !!(
      organization &&
      values.allowedOrganizations &&
      values.allowedOrganizations.length &&
      values.allowedOrganizations.includes(organization.id)
    );
  }, [organization, values.allowedOrganizations]);

  const addToProjectRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (project && addToProjectRef.current) {
      addToProjectRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, addToProjectRef.current]);

  return (
    <>
      <div className={semClasses.root}>
        <Button
          autoFocus={!values.projectId}
          variant={buttonVariant}
          onClick={() => setState({ isOpenModal: true })}
          className={semClasses.rootButton}
          size="large"
          data-cy="select-organization-project-button"
        >
          {getButtonText()}
          <ArrowRoundedIcon className={semClasses.rootButtonIcon} />
        </Button>
        {organization && project && values.projectId && (
          <div className={semClasses.rootInfo}>
            <span>{organization.name}</span>
            <span>{project.name}</span>
          </div>
        )}
      </div>
      <HUModal2
        open={isOpenModal}
        size={shouldSkipOrganizationsColumn ? 'small' : 'large'}
        title={t('labels.select_project_modal_title')}
        paperClassName={semClasses.modalPaper}
        onClose={onCancel}
        footer={({ footerClass }) => (
          <div className={footerClass}>
            <Button
              color="primary"
              variant="contained"
              disabled={!project || isDisabled}
              ref={addToProjectRef}
              onClick={onAddButtonClick}
              data-cy="select-organization-project-confirm-button"
            >
              {t('templates.add_to_project', {
                project: project?.name || '...',
              })}
            </Button>
            <Button
              color="primary"
              variant="text"
              onClick={onCancel}
              data-cy="select-organization-project-cancel-button"
            >
              {t('labels.cancel')}
            </Button>
          </div>
        )}
      >
        <div className={semClasses.body}>
          <div
            className={cx(semClasses.row, {
              [semClasses.row2Columns]: !shouldSkipOrganizationsColumn,
            })}
          >
            {!shouldSkipOrganizationsColumn && (
              <div className={semClasses.column}>
                <SelectItemCatalogue
                  options={organizationsFiltered}
                  selected={organization}
                  getOptionLabel={o => o.name}
                  getOptionSelected={(o, s) => o.id === s?.id}
                  onItemClick={(e, v) => onOrganizationSelect(v)}
                  title={t('labels.organizations')}
                  isDisabled={isDisabled}
                />
              </div>
            )}
            <div className={semClasses.column}>
              {isFetchingProjects ? (
                <AlignCenter>
                  <CircularProgress color="primary" size={40} />
                </AlignCenter>
              ) : !organization ? (
                <AlignCenter>
                  <div className={semClasses.tipText}>{t('labels.select_organization_from_left')}</div>
                </AlignCenter>
              ) : (
                <SelectItemCatalogue
                  options={projects}
                  selected={project}
                  getOptionLabel={o => o.name}
                  getOptionSelected={(o, s) => o.id === s?.id}
                  onItemClick={(e, v) => onProjectSelect(v)}
                  title={t('labels.projects')}
                  iconType="check"
                  noOptionsText={t('labels.org_does_not_have_projects')}
                  isDisabled={isDisabled}
                />
              )}
            </div>
          </div>
        </div>
      </HUModal2>
    </>
  );
}
